import React, { Component, useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import BotTabTableContainer from '../BotTabTableContainer';
import { CONSTS } from 'constants/index.js';
import QuickSearch from 'components/BuildableFilter/QuickSearch';
import FilterBuilder from 'components/BuildableFilter/FilterBuilder';
import SkillsTableQuickFilters from './SkillsTableQuickFilters';
import { Collapse } from 'ui';
import TableTeamSelect from 'components/TableTeamSelect';
import TeamSelect from 'components/TeamSelect';
import useDebounce from '../../../utilities/useDebounce';
import { useSelector } from 'react-redux';
import {
  getBotsSkills,
  getBotsSkillsFilters,
  getBotsSkillsOrder,
  getBotsSkillsOrderBy,
  getBotsSkillsPage,
  getBotsSkillsSearch,
  getBotsSkillsTagChoices,
  getBotsSkillsTotal,
} from './selectors';
import GeckoDate from 'services/gecko-date';

import { fetchSkills } from '../actions';

import {
  GECKO_CONSTS,
  GeckoTable,
  GeckoToastService,
  GeckoAlert,
  GeckoPagination,
  GeckoDialogService,
  Tooltip,
  OverlayTrigger,
} from 'ui';

import { BotTagDisplay } from '../BotTagManager';

const Skills = (props) => {
  const { botId } = props;

  const filters = useSelector(getBotsSkillsFilters);
  const order = useSelector(getBotsSkillsOrder);
  const orderBy = useSelector(getBotsSkillsOrderBy);
  const page = useSelector(getBotsSkillsPage);
  const search = useSelector(getBotsSkillsSearch);
  const skills = useSelector(getBotsSkills);
  const tagChoices = useSelector(getBotsSkillsTagChoices);
  const total = useSelector(getBotsSkillsTotal);
  const agents = useSelector((state) => state.agents);

  const [showFilters, setShowFilters] = useState(filters.length !== 0);
  const [pickedFilters, setPickedFilters] = useState(filters);
  const debouncedFilters = useDebounce(pickedFilters);

  useEffect(() => {
    fetchSkills({
      page: 1,
      botId: botId,
      order: order,
      orderBy: orderBy,
      search: search,
      filters: debouncedFilters,
    });
  }, [debouncedFilters]);

  useEffect(() => {
    fetchSkills({
      page: page ? page : 1,
      order: 'DESC',
      orderBy: 'createdAt',
      search: '',
      botId: botId,
      filters: filters,
    });
  }, []);

  const updateTeams = async (teamIds, skillId) => {
    try {
      await GeckoRequest.post(`/bots/${botId}/skills/${skillId}/update-teams`, {
        teamId: teamIds,
      });
      await fetchSkills({
        page: page,
        botId: botId,
        order: order,
        orderBy: orderBy,
        search: search,
        filters: filters,
      });
    } catch (error) {
      return renderError(error);
    }
  };

  const deleteSkill = async (row) => {
    const ConfirmedResponse = await new GeckoDialogService().confirm(
      `Are you sure you want to delete this skill?`
    );
    if (ConfirmedResponse) {
      try {
        await GeckoRequest.delete(`/bots/${botId}/skills/${row.id}`);
        new GeckoToastService().success(`${row.name} has been deleted`);
        return fetchSkills({
          page: page,
          botId: botId,
          orderBy: orderBy,
          order: order,
          filters: filters,
        });
      } catch (error) {
        return renderError(error);
      }
    }
    return false;
  };

  const cloneSkill = (skill) => {
    return GeckoLocation.goTo(
      `/bots/${botId}/skills/${
        CONSTS.BOT_SKILL_HANDOVER[skill.internal_skill] ||
        skill.internal_skill.toLowerCase()
      }/clone/${skill.id}`
    );
  };

  const editSkill = (skill) => {
    return GeckoLocation.goTo(
      `/bots/${botId}/skills/${
        CONSTS.BOT_SKILL_HANDOVER[skill.internal_skill] ||
        skill.internal_skill.toLowerCase()
      }/${skill.id}`
    );
  };

  const renderOwnerTableCell = (_successes, skill) => {
    const assignedTeams = skill.assigned_team_ids;
    const onChangeHandler = (teamIds) => updateTeams(teamIds, skill.id);
    return (
      <TableTeamSelect
        onChange={onChangeHandler}
        assignedTeamIds={assignedTeams}
        selectProps={{
          openOnClick: true,
          autoFocus: true,
          openOnFocus: true,
          escapeClearsValue: false,
        }}
      />
    );
  };

  const cols = [
    {
      key: 'name',
      name: 'Name',
      sortable: true,
      render: (_name, bot) => {
        return bot.name;
      },
    },
    {
      name: 'Owner',
      key: 'assigned_team_ids',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: renderOwnerTableCell,
    },
    {
      key: 'internal_skill',
      name: 'Skill type',
      sortable: true,
      render: (type) => {
        const typeMap = {
          DATA_CAPTURE: 'Data Capture',
          SUGGEST_ANSWER: 'Suggest Answer',
          DISPLAY_LIST: 'Display List',
          GECKO_FORM: 'Gecko Form',
          WORKFLOW_ACTION: 'Workflow Action',
        };
        return typeMap[type] || type;
      },
    },
    {
      name: 'Tags',
      key: 'bot_tag_ids',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (tag_ids, skill) => (
        <BotTagDisplay item={skill} controlled={false} />
      ),
    },
    {
      name: 'Last Updated',
      key: 'updatedAt',
      render: (val, skill) => {
        let lastUpdatedByAgent;
        let key;
        if (skill.last_updated_by) {
          lastUpdatedByAgent = agents.find(
            (agent) => agent.id === skill.last_updated_by
          );
          key = `skill-last-updated-by-${skill.id}`;
        }
        return (
          lastUpdatedByAgent ? (
          <OverlayTrigger
            placement="auto"
            overlay={
              <Tooltip
                id={key}
              >{`Edited by ${lastUpdatedByAgent.full_name}`}</Tooltip>
            }
          >
            <span id={key}>{new GeckoDate(val).LONG_TIME}</span>
          </OverlayTrigger>
        ): null
      )},
      sortable: true,
    },
    {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      width: '1%',
      items: [
        {
          name: 'Clone',
          handler: (row) => cloneSkill(row),
        },
        {
          name: 'Delete',
          handler: (row) => deleteSkill(row),
        },
      ],
    },
  ];

  const handleSearch = (search) => {
    return fetchSkills({
      page: 1,
      botId: botId,
      orderBy: orderBy,
      order: order,
      search: search,
      filters: filters,
    });
  };

  const onQuickFiltersChange = (newFilters) => {
    setShowFilters(newFilters.length !== 0);
    setPickedFilters(newFilters);
  };

  const renderHeaderChild = () => {
    return (
      <div
        style={{
          paddingLeft: '10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <SkillsTableQuickFilters
          botId={botId}
          filters={filters}
          onChange={onQuickFiltersChange}
        />
        <QuickSearch
          showExpandButton={true}
          expanded={showFilters}
          onSearch={handleSearch}
          onToggle={() => {
            setShowFilters(!showFilters);
          }}
          filterButtonName=""
        />
      </div>
    );
  };

  const pagination = {
    changeOrder: (sortKey) => {
      if (orderBy === sortKey) {
        fetchSkills({
          page: page,
          botId: botId,
          orderBy: sortKey,
          order: order === 'ASC' ? 'DESC' : 'ASC',
          search: search,
          filters: filters,
        });
      } else {
        fetchSkills({
          page: page,
          botId: botId,
          orderBy: sortKey,
          order: 'ASC',
          search: search,
          filters: filters,
        });
      }
    },
    changePage: (page) =>
      fetchSkills({
        botId: botId,
        page,
        search: search,
        filters: filters,
      }),
    current_page: page,
    order_by: orderBy,
    sort: order,
    total_items: total,
    total_pages: Math.ceil(total / 20),
  };

  const fields = [
    {
      key: 'name',
      type: 'text',
      label: 'Name',
    },
    {
      key: 'config',
      operators: [
        { label: 'is', value: 'custom:skill message is' },
        { label: 'is not', value: 'custom:skill message is not' },
        { label: 'starts with', value: 'custom:skill message starts with' },
        { label: 'ends with', value: 'custom:skill message ends with' },
        { label: 'contains', value: 'custom:skill message contains' },
        { label: 'not contains', value: 'custom:skill message not contains' },
      ],
      type: 'custom',
      label: 'Message',
      field_type: GECKO_CONSTS.FIELD_TYPE_SELECT_CREATE_MULTI,
    },
    {
      key: 'internal_skill',
      type: 'choice',
      label: 'Skill Type',
      choices: [
        { label: 'Data Capture', value: 'DATA_CAPTURE' },
        { label: 'Suggest Answer', value: 'SUGGEST_ANSWER' },
        { label: 'Display List', value: 'DISPLAY_LIST' },
        { label: 'Gecko Form', value: 'GECKO_FORM' },
        { label: 'Workflow Action', value: 'WORKFLOW_ACTION' },
      ],
    },
    {
      key: 'team',
      label: 'Team',
      type: 'custom',
      operators: [
        { label: 'is', value: 'custom:team is' },
        {
          label: 'is not',
          value: 'custom:team is not',
        },
      ],
      field: <TeamSelect />,
    },
    {
      key: 'createdAt',
      type: 'date',
      label: 'Created at',
    },
    {
      key: 'updatedAt',
      type: 'date',
      label: 'Updated at',
    },
    {
      key: 'bot_tag_ids',
      type: 'custom',
      label: 'Tags',
      choices: tagChoices,
      field_type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
      nullable: true,
      operators: [
        { label: 'contains', value: 'textarray:is' },
        { label: 'not contains', value: 'textarray:is not' },
        { label: 'is blank', value: 'textarray:is blank' },
        { label: 'is not blank', value: 'textarray:is not blank' },
      ],
    },
  ];

  const renderSkillsContents = () => {
    const hasSkills = skills && skills.length !== 0;
    if (hasSkills) {
      return (
        <div
          className={classNames('gecko-card-body', {
            'gecko-card-body--large': skills.length > 8,
            'gecko-card-body--small': skills.length <= 8,
          })}
        >
          <GeckoTable
            cols={cols}
            rows={skills}
            pagination={pagination}
            rowHandler={editSkill}
          />
          <GeckoPagination pagination={pagination} />
        </div>
      );
    }

    if (search) {
      return (
        <div className="BotTabTableContainer-table-body gecko-card-body">
          <GeckoAlert type={GECKO_CONSTS.ALERT_TYPE_WARNING}>
            No results found for the search term "{search}".
          </GeckoAlert>
        </div>
      );
    }

    return (
      <div className="BotTabTableContainer-table-body gecko-card-body">
        <GeckoAlert type={GECKO_CONSTS.ALERT_TYPE_WARNING}>
          Your bot hasn't got any skills yet.
        </GeckoAlert>
      </div>
    );
  };

  return (
    <div className="Bots">
      <BotTabTableContainer
        title="Skills"
        createText="Create skill"
        onCreate={() => GeckoLocation.goTo(`/bots/${botId}/skills/new`)}
        headerChild={renderHeaderChild()}
      >
        <Collapse in={showFilters}>
          <div>
            {showFilters ? (
              <FilterBuilder
                filters={filters}
                fields={fields}
                onChange={setPickedFilters}
              />
            ) : null}
          </div>
        </Collapse>
        {renderSkillsContents()}
      </BotTabTableContainer>
    </div>
  );
};

export default Skills;
